import React, { useMemo, useState } from 'react';
import { Form, Select, Input, Button, Space, Alert, Tag } from 'antd';
import { QuoteStatus } from '../types.ts';
import {
  QUOTE_STATUS,
  QUOTE_STATUS_DESCRIPTIONS,
  getSelectableQuoteStatusIds,
  getQuoteStatusColor,
  isQuoteTerminal,
} from '../utils/quoteStatus.ts';

const { Option } = Select;
const { TextArea } = Input;

// Cambio stato di un preventivo (cliente o fornitore): propone SOLO le transizioni
// autorizzate dal flusso condiviso (utils/quoteStatus.ts, specchio del backend).
interface QuoteStatusModalProps {
  quote: {
    id: number;
    quote_number: string;
    quote_status_id: number;
    quote_status?: QuoteStatus | null;
  } | null;
  quoteType: 'customer' | 'supplier';
  quoteStatuses: QuoteStatus[];
  onSubmit: (data: { status_id: number; notes?: string }) => void;
  onCancel: () => void;
}

const QuoteStatusModal: React.FC<QuoteStatusModalProps> = ({
  quote,
  quoteType,
  quoteStatuses,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [selectedStatusId, setSelectedStatusId] = useState<number | undefined>();

  const currentStatusId = quote?.quote_status_id;
  const selectableStatuses = useMemo(() => {
    const ids = getSelectableQuoteStatusIds(currentStatusId);
    return quoteStatuses.filter((status) => ids.includes(status.id));
  }, [quoteStatuses, currentStatusId]);
  const hasTransitions = selectableStatuses.some((status) => status.id !== currentStatusId);

  if (!quote) return null;

  const currentStatus = quote.quote_status || quoteStatuses.find((s) => s.id === quote.quote_status_id);
  const counterpartLabel = quoteType === 'customer' ? 'cliente' : 'fornitore';

  const handleSubmit = (values: any) => {
    onSubmit({ status_id: values.status_id, notes: values.notes });
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Alert
        message={`Preventivo ${counterpartLabel}: ${quote.quote_number}`}
        description={
          <span>
            Stato attuale:{' '}
            <Tag color={getQuoteStatusColor(currentStatus)}>{currentStatus?.display_name || 'N/A'}</Tag>
          </span>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      {!hasTransitions && (
        <Alert
          message="Stato definitivo"
          description={
            currentStatusId === QUOTE_STATUS.COMPLETED
              ? "Il preventivo è stato convertito in ordine: nessun cambio di stato è più possibile."
              : "Il preventivo è in uno stato definitivo: nessun cambio di stato è più possibile."
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item
        name="status_id"
        label="Nuovo Stato"
        rules={[{ required: true, message: 'Seleziona un nuovo stato' }]}
      >
        <Select
          placeholder="Seleziona nuovo stato"
          disabled={!hasTransitions}
          onChange={(value) => setSelectedStatusId(value)}
        >
          {selectableStatuses.map((status) => (
            <Option key={status.id} value={status.id} disabled={status.id === currentStatusId}>
              <Tag color={getQuoteStatusColor(status)}>{status.display_name}</Tag>
              {status.id === currentStatusId && ' (Attuale)'}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {selectedStatusId && selectedStatusId !== currentStatusId && (
        <Alert
          message="Descrizione Stato"
          description={QUOTE_STATUS_DESCRIPTIONS[selectedStatusId] || ''}
          type="info"
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedStatusId === QUOTE_STATUS.ACCEPTED && (
        <Alert
          message="Attenzione"
          description={
            quoteType === 'customer'
              ? "Accettando il preventivo le righe prodotto non saranno più modificabili e verrà verificata la disponibilità dei prodotti (avviso, non blocco). Da 'Accettato' si potrà solo convertire in ordine o cancellare."
              : "Accettando il preventivo le righe prodotto non saranno più modificabili. Da 'Accettato' si potrà solo convertire in ordine fornitore o cancellare."
          }
          type="warning"
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedStatusId && isQuoteTerminal(selectedStatusId) && (
        <Alert
          message="Attenzione"
          description="Stato definitivo: il preventivo non potrà più cambiare stato né essere modificato."
          type="warning"
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Lo stato 'Evaso' non si imposta a mano: viene assegnato automaticamente dalla conversione in ordine."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form.Item name="notes" label="Note (opzionale)">
        <TextArea rows={3} placeholder="Aggiungi note sul cambio di stato..." />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" disabled={!hasTransitions}>
            Aggiorna Stato
          </Button>
          <Button onClick={onCancel}>Annulla</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default QuoteStatusModal;
